import isEqual from '@guanghechen/fast-deep-equal'
import { destr } from 'destr'
import { showErrorDialog } from 'src/CrudManager/Core/composites/Dialogs/ErrorDialog'
import type { CrudEditorData } from 'src/CrudManager/Core/CrudModules/Editor/types'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudInstance, CrudObject } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { computed, watch } from 'vue'
import useCrudUtils from './useCrudUtils'

/**
 * Composable for shared CRUD operations with RESTful API patterns
 * @param instance The CRUD instance to work with
 * @returns Object with CRUD operations and reactive data
 */
export default function useSharedCrud(instance: CrudInstance) {
  const crudManagerStore = useCrudManagerStore()
  const { getModelConfig } = useCrudUtils()

  // Ensure that `instance` is always defined
  if (!instance) {
    throw new Error('Instance is required')
  }

  let controller = new AbortController()

  const instanceData = computed({
    get: () => instance.data as CrudEditorData,
    set: (value) => {
      instance.data = value
    },
  })

  const instanceProps = computed(() => instance.props)

  const model = computed(() => crudManagerStore.getModelMeta(instance.modelName))

  const instanceStatus = computed({
    get: () => instance.status,
    set: (value) => {
      instance.status = value
    },
  })

  const item = computed({
    get: () => (instance.data as CrudEditorData)?.item as CrudObject,
    set: (value) => {
      if (instance.data) {
        ;(instance.data as CrudEditorData).item = value as CrudObject
      }
    },
  })

  watch(
    () => (instance.data as CrudEditorData)?.item,
    () => {
      if (instanceStatus.value.isLoading || instanceStatus.value.disableSaveWatcher === true) return
      const editorData = instance.data as CrudEditorData
      if (editorData?.item && editorData?.originalItem) {
        instanceStatus.value.isAwaitingSave = !isEqual(editorData.item, editorData.originalItem)
      }
    },
    { deep: true, immediate: true },
  )

  /**
   * Gets the object from the database for the CRUD manager using RESTful API
   * @param id The ID of the object
   * @returns Promise<boolean> - true if successful, false if error occurred
   */
  const getFromDB = async (): Promise<boolean> => {
    if (controller) controller.abort()
    controller = new AbortController()

    const id = instanceData.value && instanceData.value.item && instanceData.value.item.id ? instanceData.value.item.id : null

    if (!id) {
      const defaultItemData = {}
      instanceData.value.item = destr(JSON.stringify(defaultItemData))
      instanceData.value.originalItem = destr(JSON.stringify(defaultItemData))
      return true
    }

    instanceStatus.value.isLoading = true
    try {
      // Use new RESTful API pattern: GET /{model}/{id}
      const response = await axiosApi.get(`cm/${model.value?.apiUrl || instance.modelName}/${id}`, {
        signal: controller.signal,
      })

      item.value = destr(JSON.stringify(response.data))

      // A record's company_id is a fact about that record, never an instruction
      // about the session: `show()` is deliberately unscoped, so a fiche can
      // legitimately list a document belonging to another company and reading
      // it must not re-home the user. Nothing here touches the active company —
      // only the sidebar's own control does.

      instanceData.value.originalItem = destr(JSON.stringify(response.data))

      // Update store instance title and subtitle with async config loading
      const storeInstance = crudManagerStore.crudInstances.find((i) => i.id === instance.id)
      if (storeInstance) {
        try {
          const config = await getModelConfig(instance.modelName)
          if (config) {
            const moduleConfig = config[instance.moduleName] as Record<string, unknown>
            if (moduleConfig?.getTitle) {
              const titleResult = (moduleConfig.getTitle as (data: unknown) => string | Promise<string>)(response.data)
              // Handle both sync and async getTitle functions
              storeInstance.title = titleResult instanceof Promise ? await titleResult : titleResult
            }
            if (moduleConfig?.getSubtitle) {
              const subtitleResult = (moduleConfig.getSubtitle as (data: unknown) => string | Promise<string>)(response.data)
              // Handle both sync and async getSubtitle functions
              storeInstance.subtitle = subtitleResult instanceof Promise ? await subtitleResult : subtitleResult
            }
          }
        } catch (error) {
          console.warn(`Could not load config for title/subtitle: ${instance.modelName}`, error)
        }
      }

      instanceStatus.value.isLoading = false
      return true
    } catch (error) {
      console.error('Error loading data from database:', error)
      instanceStatus.value.isLoading = false

      // Show error dialog with the raw error
      await showErrorDialog({
        error: error as Error,
        modelName: instance.modelName,
        instanceId: instance.id,
      })

      // Handle dialog close - remove the instance from the store
      const storeInstance = crudManagerStore.crudInstances.find((i) => i.id === instance.id)
      if (storeInstance) {
        crudManagerStore.removeInstance(storeInstance, false)
      }

      return false
    }
  }

  /**
   * Saves a sub-model using the new RESTful API pattern
   * @param subModel The sub-model name
   * @param subItem The sub-item data
   * @param postSaveFn Optional callback function after save
   * @returns Promise<boolean> - true if successful, false if error occurred
   */
  const saveSubModel = async (subModel: string, subItem: CrudObject, postSaveFn: ((id: string) => void) | null = null) => {
    const formParams = {
      id: instanceData.value.item.id,
      model: model.value?.name || instance.modelName,
      subModel: subModel,
      subItem: subItem,
    }
    try {
      const response = await axiosApi.post('cm/updateSubModel', formParams)
      if (!(response.data.result == 'success')) return false

      if (postSaveFn) postSaveFn(response.data.data.id)

      return true
    } catch (error) {
      return error
    }
  }

  const module = computed(() => (crudManagerStore.isInitiated ? crudManagerStore.getModule(instance.moduleName) : undefined))

  return { saveSubModel, item, instanceData, instanceProps, instanceStatus, model, module, getFromDB }
}
